/*
 * hotel-image-banner-video.js
 * ---------------------------------------------------------------------------
 * Drives the "Video" block in sections/hotel-image-banner.liquid.
 *
 * Responsibilities:
 *   1. Pick ONE transcoded rendition at runtime (viewport / Save-Data / the
 *      Quality setting) and only then set `src` — nothing is fetched otherwise.
 *      The markup ships no <source>, so this is the single load path.
 *   2. Muted autoplay with a graceful refusal path. Browsers reject play() for
 *      iOS Low Power Mode, data-saver and reduced-motion; we surface a centred
 *      play button instead of failing silently.
 *   3. Pause when scrolled out of view / the tab is hidden (bandwidth + battery).
 *   4. Unmute toggle — muting every OTHER video on the page first, so two hero
 *      banners can never talk over each other.
 *   5. Fullscreen, including the iOS Safari path (see enterFullscreen below).
 *
 * Idempotent: init() can run repeatedly (theme editor re-renders) without
 * double-binding, guarded by the `hibVideoReady` dataset flag.
 */
(function () {
  'use strict';

  var SELECTOR = '[data-hib-video]';
  var VISIBLE_RATIO = 0.35;

  function prefersReducedMotion() {
    return window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  /* Treat an explicit Save-Data header or a 2G-class connection as "don't burn
     the shopper's data on a decorative hero". Chromium-only API; absent = false. */
  function isConstrained() {
    var c = navigator.connection || navigator.mozConnection || navigator.webkitConnection;
    if (!c) return false;
    return c.saveData === true || /(^|-)2g$/.test(c.effectiveType || '');
  }

  function isHidden(el) {
    return el.offsetParent === null && getComputedStyle(el).position !== 'fixed';
  }

  /* Ordered list of renditions to try, best-fit first. Returns a LIST (not one
     URL) so a rendition that fails to load can fall through to the next. */
  var MOBILE_MQ = '(max-width: 749px)';

  /* Is there a separate portrait cut, and are we below the video breakpoint?
     750px matches the block's own desk/mob split for crop, width and zoom, so a
     banner never shows the mobile video at desktop crop values or vice versa. */
  function useMobileSet(root) {
    var d = root.dataset;
    if (!(d.mSrcSd || d.mSrcHd || d.mSrcFhd)) return false;
    return window.matchMedia(MOBILE_MQ).matches;
  }

  function sourceCandidates(root, mobile) {
    var d = root.dataset;
    var sd = (mobile ? d.mSrcSd : d.srcSd) || '';
    var hd = (mobile ? d.mSrcHd : d.srcHd) || '';
    var fhd = (mobile ? d.mSrcFhd : d.srcFhd) || '';
    var chain;

    switch (d.quality) {
      case 'sd':
        chain = [sd, hd, fhd];
        break;
      case 'hd':
        chain = [hd, fhd, sd];
        break;
      case 'fhd':
        chain = [fhd, hd, sd];
        break;
      default: {
        // Auto: phones and constrained connections get 480p (~5MB per 30s),
        // everything else 720p. 1080p is opt-in only — it is ~5x the bytes of SD
        // for a clip that is cropped by object-fit anyway.
        var small = window.matchMedia('(max-width: 749px)').matches;
        chain = small || isConstrained() ? [sd, hd, fhd] : [hd, fhd, sd];
      }
    }
    return chain.filter(function (u, i) {
      return u && chain.indexOf(u) === i;
    });
  }

  function muteOthers(current) {
    document.querySelectorAll(SELECTOR).forEach(function (other) {
      if (other === current) return;
      var v = other.querySelector('.hib-video__el');
      if (v && !v.muted) {
        v.muted = true;
        other.classList.remove('is-unmuted');
        var btn = other.querySelector('[data-hib-video-sound]');
        if (btn) {
          btn.setAttribute('aria-pressed', 'false');
          btn.setAttribute('aria-label', 'Unmute video');
        }
      }
    });
  }

  function setup(root) {
    if (root.dataset.hibVideoReady === 'true') return;

    var video = root.querySelector('.hib-video__el');
    if (!video) return;
    root.dataset.hibVideoReady = 'true';

    var soundBtn = root.querySelector('[data-hib-video-sound]');
    var fsBtn = root.querySelector('[data-hib-video-fs]');
    var playBtn = root.querySelector('[data-hib-video-play]');
    var toggleBtn = root.querySelector('[data-hib-video-toggle]');

    var wantsAutoplay = root.dataset.autoplay !== 'false';
    var pauseOffscreen = root.dataset.pauseOffscreen !== 'false';
    var honourReduced = root.dataset.reducedMotion !== 'false';
    var loaded = false;
    var inView = false;
    /* A pause the shopper asked for must STICK — otherwise the viewport observer
       restarts it the moment they scroll away and back. */
    var userPaused = false;

    function autoplayAllowed() {
      if (userPaused) return false;
      return wantsAutoplay && !(honourReduced && prefersReducedMotion());
    }

    var candidates = [];
    var candidateIndex = 0;
    var usingMobile = false;

    function load() {
      if (loaded || isHidden(root)) return;
      usingMobile = useMobileSet(root);
      candidates = sourceCandidates(root, usingMobile);
      if (!candidates.length) return;
      loaded = true;
      // preload stays "none" in the markup so nothing downloads before this
      // point. When we intend to autoplay we are fetching the file regardless,
      // so ask for enough to paint a frame even if play() is then refused.
      video.preload = autoplayAllowed() ? 'auto' : 'metadata';
      video.src = candidates[candidateIndex];
    }

    /* A rendition that 404s or won't decode must not dead-end the block: step to
       the next candidate and retry. Without this the failure is invisible —
       play() just rejects, which looks identical to a blocked autoplay. */
    video.addEventListener('error', function () {
      if (candidateIndex >= candidates.length - 1) return;
      candidateIndex += 1;
      video.src = candidates[candidateIndex];
      if (inView && autoplayAllowed()) tryPlay();
    });

    function showPlayButton(show) {
      if (!playBtn) return;
      playBtn.hidden = !show;
      root.classList.toggle('is-paused', !!show);
    }

    function tryPlay() {
      if (!loaded) load();
      if (!loaded) return;
      // Re-assert the autoplay contract; some browsers drop `muted` on a
      // src swap, which turns an allowed play() into a rejected one.
      if (!root.classList.contains('is-unmuted')) video.muted = true;
      var p = video.play();
      if (p && typeof p.then === 'function') {
        p.then(function () {
          showPlayButton(false);
        }).catch(function () {
          showPlayButton(true);
          retryOnGesture();
        });
      }
    }

    function pause() {
      if (!video.paused) video.pause();
    }

    /* A tab opened in the background (Shopify's Actions > Preview does exactly
       this) has no user gesture, and browsers defer or refuse play() there even
       for muted video. Retry once on the first real interaction so the banner
       heals itself instead of sitting blank. */
    var gestureBound = false;
    function retryOnGesture() {
      if (gestureBound) return;
      gestureBound = true;
      var events = ['pointerdown', 'touchstart', 'keydown', 'scroll'];
      var retry = function () {
        events.forEach(function (evt) {
          document.removeEventListener(evt, retry);
        });
        if (inView && autoplayAllowed() && video.paused) tryPlay();
      };
      events.forEach(function (evt) {
        document.addEventListener(evt, retry, { once: true, passive: true });
      });
    }

    /* Reveal the video (it starts at opacity 0 so whatever sits behind it — the
       hero image, or nothing — shows until there is a real frame). `loadeddata`
       alone is NOT enough: with preload="metadata" it only fires once playback
       starts, so a refused autoplay would leave the video invisible forever.
       Any of these four means we have something worth showing. */
    ['loadedmetadata', 'loadeddata', 'canplay', 'playing'].forEach(function (evt) {
      video.addEventListener(evt, function () {
        root.classList.add('is-ready');
      });
    });

    /* --- Viewport gating ------------------------------------------------- */
    if ('IntersectionObserver' in window) {
      var io = new IntersectionObserver(
        function (entries) {
          entries.forEach(function (entry) {
            inView = entry.isIntersecting && entry.intersectionRatio >= VISIBLE_RATIO;
            if (inView) {
              if (autoplayAllowed()) {
                tryPlay();
              } else {
                load();
                showPlayButton(true);
              }
            } else if (pauseOffscreen) {
              pause();
            }
          });
        },
        { threshold: [0, VISIBLE_RATIO, 1] }
      );
      io.observe(root);
    } else {
      inView = true;
      if (autoplayAllowed()) tryPlay();
      else showPlayButton(true);
    }

    /* --- Controls --------------------------------------------------------- */
    if (playBtn) {
      playBtn.addEventListener('click', function () {
        userPaused = false;
        showPlayButton(false);
        video.play().catch(function () {
          showPlayButton(true);
        });
      });
    }

    function syncToggleLabel() {
      if (toggleBtn) {
        toggleBtn.setAttribute('aria-label', video.paused ? 'Play video' : 'Pause video');
      }
    }

    if (toggleBtn) {
      toggleBtn.addEventListener('click', function () {
        if (video.paused) {
          userPaused = false;
          showPlayButton(false);
          if (!loaded) load();
          video.play().catch(function () {
            showPlayButton(true);
          });
        } else {
          userPaused = true;
          pause();
          // Reuse the centred play affordance as the "you paused this" marker.
          showPlayButton(true);
        }
        syncToggleLabel();
      });
      ['play', 'pause'].forEach(function (evt) {
        video.addEventListener(evt, syncToggleLabel);
      });
    }

    function unmute() {
      muteOthers(root);
      video.muted = false;
      root.classList.add('is-unmuted');
      if (soundBtn) {
        soundBtn.setAttribute('aria-pressed', 'true');
        soundBtn.setAttribute('aria-label', 'Mute video');
      }
      if (video.paused) tryPlay();
    }

    function mute() {
      video.muted = true;
      root.classList.remove('is-unmuted');
      if (soundBtn) {
        soundBtn.setAttribute('aria-pressed', 'false');
        soundBtn.setAttribute('aria-label', 'Unmute video');
      }
    }

    if (soundBtn) {
      soundBtn.addEventListener('click', function () {
        if (video.muted) unmute();
        else mute();
      });
    }

    /* iOS Safari on iPhone does NOT implement Element.requestFullscreen() — only
       HTMLVideoElement.webkitEnterFullscreen(), which hands over to the native
       player (our overlay buttons disappear and native controls take over). Try
       the element first so desktop/iPad keep the custom UI, fall back to the
       video-only API. A fullscreen click is a user gesture, so it is also the one
       moment we can reliably unmute. */
    if (fsBtn) {
      fsBtn.addEventListener('click', function () {
        if (!loaded) load();
        unmute();
        var native = function () {
          if (video.webkitEnterFullscreen) video.webkitEnterFullscreen();
        };
        if (root.requestFullscreen) {
          root.requestFullscreen().catch(native);
        } else if (root.webkitRequestFullscreen) {
          root.webkitRequestFullscreen();
        } else {
          native();
        }
        if (video.paused) tryPlay();
      });
    }

    function onFullscreenChange() {
      var fsEl = document.fullscreenElement || document.webkitFullscreenElement;
      root.classList.toggle('is-fullscreen', fsEl === root);
    }
    document.addEventListener('fullscreenchange', onFullscreenChange);
    document.addEventListener('webkitfullscreenchange', onFullscreenChange);

    /* --- Tab visibility --------------------------------------------------- */
    document.addEventListener('visibilitychange', function () {
      if (document.hidden) {
        pause();
      } else if (inView && autoplayAllowed()) {
        tryPlay();
      }
    });

    /* Two resize concerns:
         - the block can be hidden on mobile ("Show on mobile" off), so if the
           viewport grows into a size where it becomes visible, load on demand;
         - with a separate mobile video, crossing 750px must swap the source.
       The swap preserves whether it was playing; currentTime is not carried over
       because the two cuts are different edits, so a shared timestamp would be
       meaningless. */
    function onResize() {
      if (!loaded) {
        if (inView && !isHidden(root) && autoplayAllowed()) tryPlay();
        return;
      }
      var wantMobile = useMobileSet(root);
      if (wantMobile === usingMobile) return;
      var wasPlaying = !video.paused;
      usingMobile = wantMobile;
      candidates = sourceCandidates(root, usingMobile);
      candidateIndex = 0;
      if (!candidates.length) return;
      root.classList.remove('is-ready');
      video.src = candidates[0];
      if (wasPlaying || (inView && autoplayAllowed())) tryPlay();
    }
    window.addEventListener('resize', onResize, { passive: true });
    window.addEventListener('orientationchange', onResize, { passive: true });
  }

  function init(scope) {
    (scope || document).querySelectorAll(SELECTOR).forEach(setup);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      init();
    });
  } else {
    init();
  }

  /* Theme editor: sections are re-rendered wholesale, so re-init the new DOM.
     Selecting a block pauses playback so the merchant can see the frame they are
     positioning rather than a moving target. */
  document.addEventListener('shopify:section:load', function (e) {
    init(e.target);
  });
  document.addEventListener('shopify:block:select', function (e) {
    var v = e.target.querySelector && e.target.querySelector('.hib-video__el');
    if (v) v.pause();
  });
})();
